export const VALIDATION_PATTERNS = {
  NAME: /^[A-Za-z]+(?:\s[A-Za-z]+)*$/,
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  // PASSWORD: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/,
  PASSWORD: /^.*$/,
  PHONE: /^\d{10}$/,
  OLD_PASSWORD: /^.*$/,
  NEW_PASSWORD: /^.*$/,
  CONFIRM_PASSWORD: /^.*$/,
} as const;

export type ValidationKey = keyof typeof VALIDATION_PATTERNS;

// Common message type
type ValidationMessage = {
  required?: string;
  pattern?: string;
  minLength?: string;
  maxLength?: string;
  matchField?: string;
};

//  Messages
export const VALIDATION_MESSAGES: Record<ValidationKey, ValidationMessage> = {
  NAME: {
    required: "Name is required",
    pattern: "Only alphabets allowed (no spaces or special characters)",
  },
  EMAIL: {
    required: "Email is required",
    pattern: "Enter a valid email address",
  },
  PASSWORD: {
    required: "Password is required",
    minLength: "Password must be at least 8 characters",
    maxLength: "Password must not exceed 16 characters",
    pattern: "Password must contain letters, numbers and special characters",
  },
  PHONE: {
    required: "Phone number is required",
    minLength: "Phone number must be 10 digits",
    maxLength: "Phone number must be 10 digits",
    pattern: "Phone number must be exactly 10 digits",
  },
  OLD_PASSWORD: {
    required: "Old password is required",
    minLength: "Old password must be at least 5 characters",
    maxLength: "Old password must not exceed 16 characters",
    pattern: "Old password must contain letters, numbers and special characters",
  },
  NEW_PASSWORD: {
    required: "New password is required",
    minLength: "New password must be at least 5 characters",
    maxLength: "New password must not exceed 16 characters",
    pattern: "New password must contain letters, numbers and special characters",
  },
  CONFIRM_PASSWORD: {
    required: "Confirm password is required",
    minLength: "Confirm password must be at least 5 characters",
    maxLength: "Confirm password must not exceed 16 characters",
    pattern: "Confirm password must contain letters, numbers and special characters",
    matchField: "Confirm password must match new password",
  },
};

// Field rules
export interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: ValidationKey | string; // allow JSON string
  minLength?: number;
  maxLength?: number;
  matchField?: string;
}

//  Core validation function
export const validateValue = (
  value: string,
  rules: FieldValidation,
  formData?: Record<string, any>,
): string | null => {
  const trimmed = value?.trim() || "";
  const label = rules.label || "Field";

  // pattern key
  const key =
    rules.pattern && rules.pattern in VALIDATION_PATTERNS
      ? (rules.pattern as ValidationKey)
      : undefined;

  const messages = key ? VALIDATION_MESSAGES[key] : {};

  // Required
  if (rules.required && !trimmed) {
    return messages.required || `${label} is required`;
  }

  // Min Length
  if (rules.minLength && trimmed.length < rules.minLength) {
    return (
      messages.minLength ||
      `${label} must be at least ${rules.minLength} characters`
    );
  }

  // Max Length
  if (rules.maxLength && trimmed.length > rules.maxLength) {
    return (
      messages.maxLength ||
      `${label} must be at most ${rules.maxLength} characters`
    );
  }

  // Pattern
  if (key) {
    const regex = VALIDATION_PATTERNS[key];

    if (!regex.test(trimmed)) {
      return messages.pattern || `Invalid ${label}`;
    }
  }

  if (rules.matchField && formData) {
    const matchValue = String(formData[rules.matchField] || "").trim();
    if (trimmed !== matchValue) {
      return messages.matchField || `${label} does not match`;
    }
  }

  return null;
};
